import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getURI } from '../../api/apiConfig';
import { UserContext } from '../../context/UserContext';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import { makeStyles } from '@mui/styles';
import { PromptAlert } from 'features/ux/RouterPrompt';
import SweetAlert from 'react-bootstrap-sweetalert';
let pendingSwitchEnvironment = null;

import {
  ThemedPrimaryBtn,
  ThemedSecondaryBtn,
} from '@themes/defaultThemeProps';
import { useLazyGetLocationsQuery } from 'api/location.api';
import {
  useLazyGetParameterListsQuery,
  useLazyGetTemplateSetsQuery,
} from 'api/parameter.api';
import { useLazyGetReferenceListsQuery } from 'api/reference-list.api';
import { useLazyGetRolesQuery } from 'api/role.api';
import { useLazyGetSessionQuery } from 'api/session.api';
import {
  tenantApi,
  useLazyGetTenantQuery,
  useLazyGetTenantStatsQuery,
} from 'api/tenant.api';
import commonStyle from 'assets/jss/material-dashboard-pro-react/commonStyle';
import { retrieveNotifications } from 'features/notification/notificationSlice';
import { selectViewIsDirty, setViewIsDirty } from 'features/ux/uxSlice';
import dashboardDates from 'views/Dashboard/dashboardDates.const';
import { selectClientTenants } from '../../features/client/clientSlice';
import {
  selectClient,
  setImpersonatedTenant,
  setImpersonationSessionData,
  setToken,
} from '../../features/session/sessionSlice';
import {
  selectNtAdministrator,
  selectRoles,
} from '../../features/user/userSlice';

const SelectEnvironment = (props) => {
  const useStyles = makeStyles((theme) => ({
    ...commonStyle,
    margin: {
      margin: theme.spacing(1),
    },
  }));

  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const clientTenants = useSelector(selectClientTenants);
  const userEnvironments = useSelector(selectRoles);
  const ntAdministrator = useSelector(selectNtAdministrator);
  const client = useSelector(selectClient);
  const viewIsDirty = useSelector(selectViewIsDirty);

  const [environment, setEnvironment] = useState(0);
  const [showDirtySwitchAlert, setShowDirtySwitchAlert] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [userContext, setUserContext] = useContext(UserContext);

  const [triggerGetSession] = useLazyGetSessionQuery();
  const [triggerGetTenant] = useLazyGetTenantQuery();
  const [triggerGetTenantStats] = useLazyGetTenantStatsQuery();
  const [triggerGetParameterLists] = useLazyGetParameterListsQuery();
  const [triggerGetTemplateSets] = useLazyGetTemplateSetsQuery();
  const [triggerGetRoles] = useLazyGetRolesQuery();
  const [triggerGetLocations] = useLazyGetLocationsQuery();
  const [triggerGetReferenceLists] = useLazyGetReferenceListsQuery();

  const initializeImpersonationSession = async (token) => {
    const sessionData = await triggerGetSession({ token }).unwrap();

    dispatch(setToken(sessionData?.token));
    const period = dashboardDates[0].replace(/\s+/g, '').toLowerCase();
    const tenantData = await triggerGetTenant().unwrap();
    dispatch(setImpersonatedTenant(tenantData));
    await dispatch(tenantApi.util.invalidateTags(['Tenant', period]));

    // Run the remaining operations in parallel for better performance
    await Promise.all([
      triggerGetTenantStats({ period }).unwrap(),
      triggerGetRoles().unwrap(),
      triggerGetReferenceLists().unwrap(),
      triggerGetLocations({ limit: 20 }).unwrap(),
      triggerGetParameterLists().unwrap(),
      triggerGetTemplateSets().unwrap(),
      dispatch(retrieveNotifications()),
    ]);
    dispatch(setImpersonationSessionData(sessionData));
  };

  const administratorEnvironments = clientTenants.filter(
    (environment) =>
      environment.status === 'active' || environment.status === 'new'
  );

  const environments = ntAdministrator
    ? administratorEnvironments
    : userEnvironments;

  const environmentList = () => {
    const list = environments.map((entry, index) => {
      const tenant = clientTenants.find(
        (tenant) => tenant.tenant_id === entry.tenant_id
      );
      const tenantLabel = tenant ? tenant.tenant_label : '';
      return (
        <MenuItem key={index} value={index}>
          {tenantLabel}
        </MenuItem>
      );
    });
    return list;
  };

  const switchEnvironment = async (environment) => {
    if (viewIsDirty) {
      pendingSwitchEnvironment = environment;
      setShowDirtySwitchAlert(true);
      props.setEnvironmentSelectOpen(false);
      return;
    }

    setIsSwitching(true);
    const tenant = clientTenants.find(
      (tenant) => tenant?.tenant_id === environment?.tenant_id
    );
    dispatch(setImpersonatedTenant(tenant));

    const body = { client_id: client._id, tenant_id: tenant?.tenant_id };
    fetch(getURI('switchtenant'), {
      // change this to fetchwithtimeout
      method: 'POST',
      body: JSON.stringify(body),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', token: userContext.token },
    }).then(async (response) => {
      try {
        if (response.ok) {
          const data = await response.json();
          setUserContext((oldValues) => {
            return { ...oldValues, token: data.token };
          });
          await initializeImpersonationSession(data.token); // configure the new impersonation session
          pendingSwitchEnvironment = null;
          props.switchEnvironment();
          history.push('/admin/dashboard');
        } else {
          setUserContext((oldValues) => {
            return { ...oldValues, token: null };
          });
        }
      } finally {
        setIsSwitching(false);
      }
    });
  };

  useEffect(() => {
    if (pendingSwitchEnvironment !== null) {
      switchEnvironment(pendingSwitchEnvironment);
    }
  }, [viewIsDirty]);

  return (
    <>
      {isSwitching && (
        <SweetAlert
          style={{ display: 'block', marginTop: '-100px' }}
          title={<span style={{ color: '#3c4858' }}>Switching Environment!</span>}
          showConfirm={false}
          onConfirm={() => {}}
        >
          <span style={{ color: '#3c4858' }}>
            Please wait while we load the new environment...
          </span>
        </SweetAlert>
      )}
      {showDirtySwitchAlert && (
        <PromptAlert
          onConfirm={() => {
            setShowDirtySwitchAlert(false);
            dispatch(setViewIsDirty(false));
          }}
          onCancel={() => {
            setShowDirtySwitchAlert(false);
            pendingSwitchEnvironment = null;
          }}
        />
      )}
      <Dialog
        open={props.environmentSelectOpen && !isSwitching}
        onClose={() => props.setEnvironmentSelectOpen(false)}
        aria-labelledby='form-dialog-title'
      >
        <DialogTitle id='form-dialog-title'>Select Environment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select an environment to use during this session.
          </DialogContentText>
          <FormControl
            className={classes.margin}
            variant='outlined'
            style={{ minWidth: 200 }}
          >
            <InputLabel
              id='default-client-label'
              className={classes.inputLabel}
            >
              Environment
            </InputLabel>
            <Select
              variant='standard'
              className={classes.select}
              labelId='default-environment-label'
              id='environment'
              value={environment}
              onChange={(event) => setEnvironment(event.target.value)}
              label='Environment'
            >
              {environmentList()}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <ThemedSecondaryBtn
            onClick={() => props.setEnvironmentSelectOpen(false)}
          >
            Cancel
          </ThemedSecondaryBtn>
          <ThemedPrimaryBtn
            onClick={() => switchEnvironment(environments[environment])}
            color='primary'
          >
            Switch
          </ThemedPrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SelectEnvironment;
